import React from 'react';
import './css/epk.css';
import './css/yacht-rock-band.css';

import PhotoGallery from './sub-components/PhotGallery';
import Review from './sub-components/review';
import Songlist from "./sub-components/Songlist";
import { Helmet } from "react-helmet-async";
import EPKCalendar from "./sub-components/EPKCalendar";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFacebook, faInstagram } from "@fortawesome/free-brands-svg-icons";

import ContactForm from "./sub-components/ContactForm";

import TodImg from './images/band/tod_tugboat.webp';
import JayImg from './images/band/jay_captain_riff.webp';
import DeanImg from './images/band/dean_deckhand.webp';
import DaveImg from './images/band/dave_windjammer.webp';
import MikeImg from './images/band/michael_keys.webp';
import GaryImg from './images/band/gary_anchor.webp';

// Keep these in sync with the "/yacht-rock-band" entry in SeoPageRenderer.cs
const PAGE_URL = "https://www.dysband.com/yacht-rock-band";
const PAGE_TITLE = "Yacht Rock Band in Phoenix | Dirty Yachtin' Scoundrels";
const PAGE_DESCRIPTION =
    "Hire Dirty Yachtin' Scoundrels, a Phoenix yacht rock band playing smooth 70s & 80s hits for corporate events, weddings, venues, and festivals.";
const PAGE_IMAGE =
    "https://www.dysband.com/media/yacht_rock_band_dirty_yachtin_scoundrels_live.webp";

const bandMembers = [
    {
        name: "Tod 'Tidal Wave' Miller",
        role: "Lead Vocalist",
        bio: "With a voice as smooth as the ocean breeze, Tod anchors the signature sound of this premier Yacht Rock Band.",
        photo: TodImg,
    },
    {
        name: "Jay 'Captain Riff' Poole",
        role: "Lead Guitar & Backup Vocals",
        bio: "Jay’s signature guitar lines and lush harmonies help define the modern yacht rock sound.",
        photo: JayImg,
    },
    {
        name: "Dean 'Yacht Shot' Miller",
        role: "Lead Guitar & Backup Vocals",
        bio: "Dean blends smooth melodic phrasing with powerful harmonies, creating the polished sound Yacht Rock is known for.",
        photo: DeanImg,
    },
    {
        name: "Dave 'Windjammer' Miller",
        role: "Bass Guitar & Backup Vocals",
        bio: "Dave delivers deep, velvety bass lines and harmonies that embody classic soft-rock energy.",
        photo: DaveImg,
    },
    {
        name: "Gary 'Anchor' Sanchez",
        role: "Drums & Percussion",
        bio: "Gary keeps every yacht rock groove steady and dynamic with his signature rhythmic precision.",
        photo: GaryImg,
    },
    {
        name: "Michael 'Keys' Berrelleza",
        role: "Keyboards",
        bio: "Michael adds smooth textures and warm vintage synth tones—staples of the Yacht Rock Band sound.",
        photo: MikeImg,
    },
];

const YachtRockBandPage = () => {

    const schema = {
        "@context": "https://schema.org",
        "@type": "MusicGroup",
        "@id": "https://www.dysband.com/#band", // same band entity as BandSchema.js; this adds the members
        "name": "Dirty Yachtin' Scoundrels",
        "genre": ["Yacht Rock", "Soft Rock", "Classic Rock"],
        "description": "A professional Yacht Rock Band performing smooth 70s & 80s soft rock hits for events, festivals, and private parties.",
        "url": "https://www.dysband.com/",
        "member": bandMembers.map(({ name, role }) => ({
            "@type": "Person",
            "name": name,
            "jobTitle": role
        })),
        "image": PAGE_IMAGE
    };

    return (
        <>
            <Helmet>
                <title>{PAGE_TITLE}</title>
                <meta name="description" content={PAGE_DESCRIPTION} />

                {/* Open Graph */}
                <meta property="og:title" content={PAGE_TITLE} />
                <meta property="og:description" content={PAGE_DESCRIPTION} />
                <meta property="og:image" content={PAGE_IMAGE} />
                <meta property="og:url" content={PAGE_URL} />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="Dirty Yachtin' Scoundrels" />

                {/* Canonical */}
                <link rel="canonical" href={PAGE_URL} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={PAGE_TITLE} />
                <meta name="twitter:description" content={PAGE_DESCRIPTION} />
                <meta name="twitter:image" content={PAGE_IMAGE} />

                {/* Structured Data */}
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            </Helmet>

            <div className="epk backer">
                <div className="col-12 bio">

                    {/* Social */}
                    <div className="top-social">
                        <div className="facebook">
                            <a href="https://www.facebook.com/DYSYachtRock" target="_blank" rel="noopener noreferrer" aria-label="Dirty Yachtin' Scoundrels on Facebook">
                                <FontAwesomeIcon icon={faFacebook} />
                            </a>
                        </div>
                        <div className="instagram">
                            <a href="https://instagram.com/dirtyyachtin" target="_blank" rel="noopener noreferrer" aria-label="Dirty Yachtin' Scoundrels on Instagram">
                                <FontAwesomeIcon icon={faInstagram} />
                            </a>
                        </div>
                    </div>

                    <h1 className="bio_title">Dirty Yachtin' Scoundrels</h1>
                    <h2>The Premier Yacht Rock Band Experience in Phoenix, Arizona</h2>

                    <div className="hl"></div>

                    {/* Video */}
                    <h3>Video</h3>
                    <div className="video-container">
                        <iframe
                            width="560"
                            height="315"
                            src="https://www.youtube.com/embed/8C71uXWOyaQ?si=2WT3Dkhn2_iX-wKP"
                            title="Yacht Rock Band Live Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                        ></iframe>
                    </div>

                    <div className="hl"></div>

                    {/* Bio Section */}
                    <h3>About This Yacht Rock Band</h3>
                    <p className="bio_text">
                        The Dirty Yachtin' Scoundrels are a high-energy, professional Yacht Rock Band specializing in smooth
                        70s and 80s soft rock hits. With rich vocals, polished musicianship, and authentic retro vibes, the
                        band recreates the golden era of Yacht Rock with unmatched accuracy.
                    </p>

                    <p className="bio_text">
                        From Toto, Steely Dan, Michael McDonald, and Kenny Loggins to Hall & Oates and Christopher Cross,
                        the band performs the iconic tracks that define the genre. Audiences love the soaring harmonies,
                        captivating arrangements, and irresistible grooves that make every performance unforgettable.
                    </p>

                    <p className="bio_text">
                        Perfect for corporate events, private parties, festivals, weddings, and venue bookings, Dirty
                        Yachtin' Scoundrels deliver a premium yacht rock experience that keeps crowds dancing, singing, and
                        smiling all night long.
                    </p>

                    <p className="bio_text">
                        Based in Phoenix, Arizona, Dirty Yachtin' Scoundrels performs at events in the Phoenix area, across
                        Arizona, and nationwide. Check the upcoming shows below, or use the contact form at the bottom of
                        this page to ask about your date.
                    </p>

                    {/* Meet the band (these members are also listed in the structured data above) */}
                    <h3>Meet the Band</h3>
                    <div className="yrb-members">
                        {bandMembers.map(({ name, role, bio, photo }) => (
                            <div className="yrb-member" key={name}>
                                <img
                                    src={photo}
                                    alt={`${name}, ${role} of Dirty Yachtin' Scoundrels`}
                                    loading="lazy"
                                />
                                <h4>{name}</h4>
                                <p className="yrb-member-role">{role}</p>
                                <p>{bio}</p>
                            </div>
                        ))}
                    </div>

                    <PhotoGallery />

                    <div className="hl"></div>

                    <Review />

                    <div className="hl"></div>

                    {/* EPK + Setlist */}
                    <div className="epk-container">
                        <div className="epk-left">
                            <Songlist />
                        </div>
                        <div className="epk-right">
                            <EPKCalendar />
                        </div>
                    </div>

                    <h3 className="mt-5">Contact Us</h3>
                    <ContactForm />

                </div>
            </div>
        </>
    );
};

export default YachtRockBandPage;